import React, { useEffect, useRef, useState } from 'react';

import { useTranslation } from 'react-i18next';

import PropTypes from 'prop-types';

import { Editor, useEditor } from '@craftjs/core';

import TedTheme from '@common/providers/ThemeProvider/TedTheme';
import { BorderProvider } from '@common/providers/BorderProvider/BorderProvider';

import Button from '@resolvers/common/Button/Button';

import GridCell from '@resolvers/pdf/GridCellForContainer/GridCell';
import TableCell from '@resolvers/common/Table/components/TableCell';
import Container from '@resolvers/pdf/Container/Container';
import { CanvasImage } from '@resolvers/common/Images/CraftImage';
import Table from '@resolvers/common/Table/Table';
import Template from '@resolvers/pdf/Template/Template';

import TemplateEmail from '@resolvers/email/Template/TemplateEmail';
import EmailParagraph from '@resolvers/pdf/Paragraph/EmailParagraph';
import EmailContainer from '@resolvers/email/Container/EmailContainer';
import EmailContainerRow from '@resolvers/email/Container/EmailContainerRow';
import EmailContainerColumn from '@resolvers/email/Container/EmailContainerColumn';

import Placeholder from '@resolvers/common/Placeholder/Placeholder';

import FrameEditor from './FrameEditor';
import RenderNode from './RenderNode';
import TabPanel from './TabPanel/TabPanel';

import styles from './CraftEditor.module.scss';
import TableRow from '@resolvers/common/Table/components/TableRow';
import TableHeader from '@resolvers/common/Table/components/TableHeader';
import TableFooter from '@resolvers/common/Table/components/TableFooter';
import PdfParagraph from '@resolvers/email/Paragraph/PdfParagraph';
import UndoRedoButtons from '@resolvers/tools/UndoRedoBuutons/UndoRedoButtons';

import {
  isTemplatePdf,
  checkPageSize,
  validateDomForSave,
  getErrorMessages,
  mapErrorMessages,
} from '@common/helpers/helpers';

import { PageLayout } from '@resolvers/pdf/Template/PageLayout';
import PageHeader from '@resolvers/pdf/Template/PageHeader';
import PageBody from '@resolvers/pdf/Template/PageBody';
import PageFooter from '@resolvers/pdf/Template/PageFooter';
import Cards from '@resolvers/pdf/Cards/Cards';
import { useTemplateDetailsStore } from '@store/templateDetailsStore';
import { useLocation } from 'react-router-dom';
import { PAGE_ROUTES } from '@common/constants';

const isCreate = (location) => {
  return location.pathname === PAGE_ROUTES.NEW_TEMPLATE;
};

const SetEditorStateOnUnmount = ({ frameEditorRef, editorLanguage }) => {
  const { t } = useTranslation();
  const { query } = useEditor();

  const setEditorDataOnUnmount = useTemplateDetailsStore(
    (s) => s.setEditorDataOnUnmount,
  );
  const updateHtmlDataOnUnmount = () => {
    const {
      invalidRows,
      invalidTableSource,
      invalidCardsSource,
      invalidPlaceholderSource,
    } = validateDomForSave(query);

    let isValid = true;
    let errorMessages = [];

    if (
      invalidRows?.length ||
      invalidTableSource ||
      invalidCardsSource ||
      invalidPlaceholderSource
    ) {
      isValid = false;
      const errorMessageKeys = getErrorMessages({
        invalidRows,
        invalidTableSource,
        invalidCardsSource,
        invalidPlaceholderSource,
      });

      errorMessages = mapErrorMessages({ errorMessageKeys, t });
    }

    setEditorDataOnUnmount({
      HTMLdata: frameEditorRef.current?.innerHTML,
      jsonData: query.serialize(),
      language: editorLanguage,
      isValid,
      errorMessages,
    });
  };

  useEffect(() => {
    return () => updateHtmlDataOnUnmount();
  }, []);
  return;
};

const CraftEditor = ({
  jsonData,
  setSaveLoading,
  templateSubject,
  isCustomEmail,
  currentLanguage,
}) => {
  const frameEditorRef = useRef(null);
  const location = useLocation();
  const isCreateMode = isCreate(location);

  const type = useTemplateDetailsStore((s) => s.type);
  const pdfAdditionalFields = useTemplateDetailsStore(
    (s) => s.pdfAdditionalFields,
  );

  const [pageSize] = useState(
    isTemplatePdf(type) &&
      checkPageSize({
        format: pdfAdditionalFields?.format,
        orientation: pdfAdditionalFields?.orientation,
      }),
  );
  const [ready, setReady] = useState(false);
  const [editorLanguage] = useState(currentLanguage);

  useEffect(() => {
    // ! change this if 'state update while rendering' conflict appears again
    setTimeout(() => setReady(true), 1500);
  }, []);

  return (
    <div className={styles.craftEditorContainer}>
      <BorderProvider>
        <Editor
          indicator={{
            success: 'rgba(0, 123, 255, 0.6)',
            error: '#e34850',
            thickness: 10,
            transition: 'all 0.2s ease-in-out',
            style: {
              position: 'absolute',
              transition: 'all 0.2s ease',
            },
          }}
          resolver={{
            Button,
            PdfContainer: Container,
            GridCell,
            CanvasImage,
            TableCell,
            TableRow,
            TableHeader,
            TableFooter,
            Placeholder,
            Table,
            TemplatePdf: Template,
            Cards,
            PageLayout,
            PageHeader,
            PageBody,
            PageFooter,
            TemplateEmail,
            EmailParagraph,
            EmailContainer,
            EmailContainerRow,
            EmailContainerColumn,
            PdfParagraph,
          }}
          onRender={(props) => <RenderNode {...props} />}
          history={{
            enabled: true,
            maxLength: 100,
          }}
          enabled={true}
        >
          <SetEditorStateOnUnmount
            frameEditorRef={frameEditorRef}
            editorLanguage={editorLanguage}
          />
          <div className={styles.gridWrapper}>
            <div
              className={`${
                isCreateMode ? styles.columnStepper : styles.column
              } ${isCustomEmail && styles.columnCustomEmail} craft-221`}
            >
              <div
                className={
                  isCustomEmail
                    ? styles.undoRedoContainerCustomEmail
                    : isCreateMode
                      ? styles.undoRedoContainerCreate
                      : styles.undoRedoContainer
                }
              >
                <UndoRedoButtons />
              </div>

              <FrameEditor
                jsonData={jsonData}
                templateType={type}
                frameEditorRef={frameEditorRef}
                isCustomEmail={isCustomEmail}
                pageSize={pageSize}
              />
            </div>

            <div
              className={`${isCreateMode ? styles.tabStepper : styles.tab} ${
                isCustomEmail && styles.tabCustomEmail
              }`}
            >
              <TedTheme>
                <TabPanel
                  templateType={type}
                  frameEditorRef={frameEditorRef}
                  ready={ready}
                  setSaveLoading={setSaveLoading}
                  templateSubject={templateSubject}
                  isCustomEmail={isCustomEmail}
                  isCreateMode={isCreateMode}
                  currentLanguage={currentLanguage}
                />
              </TedTheme>
            </div>
          </div>
        </Editor>
      </BorderProvider>
    </div>
  );
};

CraftEditor.propTypes = {
  jsonData: PropTypes.string.isRequired,
  setSaveLoading: PropTypes.func,
};

export default CraftEditor;
